<template>
    <div class="tickets-board-column flex-shrink-0">
        <v-card class="column-card" outlined>
            <v-card-title class="py-2 px-3 text-subtitle-2 d-flex align-center">
                <span class="flex-grow-1 text-truncate">{{ statusLabel }}</span>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            icon
                            x-small
                            class="mr-1 flex-shrink-0"
                            v-bind="attrs"
                            v-on="on"
                            :color="cardsInverted ? 'primary' : undefined"
                            :aria-pressed="cardsInverted"
                            @click.stop="$emit('toggle-cards-invert')"
                        >
                            <v-icon small>mdi-swap-vertical</v-icon>
                        </v-btn>
                    </template>
                    <span>{{ $t('ticket.view.invertColumnCards') }}</span>
                </v-tooltip>
                <v-chip x-small color="primary" text-color="white" class="flex-shrink-0">{{ total }}</v-chip>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text
                ref="container"
                class="pa-2 column-cards-container"
                :class="{ 'column-cards-container--drop-target': dropHighlight }"
                @dragover.prevent="onColumnDragOver"
                @drop.prevent="onColumnDrop"
                @scroll.passive="onCardsScroll"
            >
                <template v-if="loading">
                    <v-skeleton-loader type="list-item-two-line" v-for="n in 3" :key="'skeleton-' + n" class="mb-2"></v-skeleton-loader>
                </template>
                <template v-else>
                    <div v-if="espacoTopo > 0" :style="{ height: espacoTopo + 'px' }" aria-hidden="true"></div>
                    <ticket-card
                        v-for="t in cardsVisiveis"
                        ref="cards"
                        :key="t.id_ticket"
                        :ticket="t"
                        :status-drag-enabled="statusDragEnabled"
                        :show-print="showPrint"
                        @click="$emit('card-click', t)"
                        @print="$emit('print-ticket', t)"
                        @dragover.native.prevent="onColumnDragOver"
                        @drop.native.prevent="onColumnDrop"
                    />
                    <div v-if="espacoFim > 0" :style="{ height: espacoFim + 'px' }" aria-hidden="true"></div>
                    <v-skeleton-loader v-if="carregandoMais" type="list-item-two-line" class="mb-2"></v-skeleton-loader>
                </template>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import { rotuloCatalogo } from '@/utils/ticketCatalogoLabel';
import TicketCard from './TicketCard.vue';

/** Distância do fim da coluna (px) em que a próxima página já é pedida. */
const CARREGAR_MAIS_LIMIAR_PX = 120;
/** Folga (px) acima e abaixo da área visível em que os cards continuam montados. */
const JANELA_FOLGA_PX = 600;
/** Altura estimada de um card ainda não renderizado/medido. */
const ALTURA_CARD_ESTIMADA_PX = 96;

export default {
    name: 'TicketsBoardColumn',
    components: { TicketCard },
    props: {
        status: {
            type: Object,
            required: true
        },
        /** Cards já carregados, na ordem do servidor (a inversão é feita na consulta). */
        tickets: {
            type: Array,
            default: () => []
        },
        /** Total da coluna conforme os filtros (pode ser maior que os cards carregados). */
        total: {
            type: Number,
            default: 0
        },
        /** Próxima página desta coluna em andamento. */
        carregandoMais: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        /** When true, card order in this column is reversed (per-column toggle; parent reloads the column). */
        cardsInverted: {
            type: Boolean,
            default: false
        },
        statusDragEnabled: {
            type: Boolean,
            default: false
        },
        showPrint: {
            type: Boolean,
            default: false
        },
        /** Coordinated by TicketsBoard while a ticket is dragged over this column. */
        dropHighlight: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return { janelaInicio: 0, janelaFim: 0, espacoTopo: 0, espacoFim: 0 };
    },
    computed: {
        /** Só os cards perto da área visível ficam no DOM; os já passados são desmontados (espaçadores mantêm a rolagem). */
        cardsVisiveis() {
            return (this.tickets || []).slice(this.janelaInicio, this.janelaFim);
        },
        statusLabel() {
            return rotuloCatalogo(this, this.status, 'status_ticket') || this.status.label || String(this.status.id_status_ticket || '');
        },
        temMais() {
            return (this.tickets || []).length < this.total;
        }
    },
    watch: {
        tickets() {
            this.recalcularJanela();
        },
        // Página chegou (ou a carga inicial terminou) e a coluna ainda não tem rolagem: pede a próxima.
        loading(val) {
            if (!val) this.$nextTick(this.pedirMaisSePertoDoFim);
        },
        carregandoMais(val) {
            if (!val) this.$nextTick(this.pedirMaisSePertoDoFim);
        }
    },
    created() {
        // Altura medida de cada card (por id), fora da reatividade.
        this.alturas = {};
        this.recalcularJanela();
    },
    mounted() {
        this.recalcularJanela();
        this.$nextTick(this.pedirMaisSePertoDoFim);
    },
    updated() {
        this.medirCards();
    },
    beforeDestroy() {
        if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
    },
    methods: {
        /** Rolagem infinita, mesmo esquema do corpo virtual do MerusDataTable: scroll do container com rAF. */
        onCardsScroll() {
            if (this.scrollRaf) return;
            this.scrollRaf = requestAnimationFrame(() => {
                this.scrollRaf = null;
                this.recalcularJanela();
                this.pedirMaisSePertoDoFim();
            });
        },
        /** v-card-text é funcional no Vuetify 2: o ref já é o elemento (não há $el). */
        containerEl() {
            const ref = this.$refs.container;
            return ref ? ref.$el || ref : null;
        },
        alturaDe(ticket) {
            return this.alturas[ticket.id_ticket] || ALTURA_CARD_ESTIMADA_PX;
        },
        /** Guarda a altura real (com margens) dos cards montados para dimensionar os espaçadores. */
        medirCards() {
            (this.$refs.cards || []).forEach((c, i) => {
                const el = c && c.$el;
                const t = this.cardsVisiveis[i];
                if (!el || !t || !el.getBoundingClientRect) return;
                const cs = window.getComputedStyle(el);
                const h = el.getBoundingClientRect().height + (parseFloat(cs.marginTop) || 0) + (parseFloat(cs.marginBottom) || 0);
                if (h > 0) this.alturas[t.id_ticket] = h;
            });
        },
        recalcularJanela() {
            const lista = this.tickets || [];
            const el = this.containerEl();
            const altura = el ? el.clientHeight || 800 : 800;
            const topo = el ? el.scrollTop : 0;
            const min = topo - JANELA_FOLGA_PX;
            const max = topo + altura + JANELA_FOLGA_PX;
            let acc = 0;
            let inicio = -1;
            let fim = lista.length;
            let antes = 0;
            let depoisDe = 0;
            for (let i = 0; i < lista.length; i++) {
                const h = this.alturaDe(lista[i]);
                if (inicio < 0 && acc + h > min) {
                    inicio = i;
                    antes = acc;
                }
                if (fim === lista.length && acc > max) {
                    fim = i;
                    depoisDe = acc;
                }
                acc += h;
            }
            if (inicio < 0) {
                inicio = Math.max(0, lista.length - 1);
                antes = Math.max(0, acc - (lista.length ? this.alturaDe(lista[inicio]) : 0));
            }
            if (fim === lista.length) depoisDe = acc;
            const total = acc;
            this.janelaInicio = inicio;
            this.janelaFim = fim;
            this.espacoTopo = antes;
            this.espacoFim = Math.max(0, total - depoisDe);
        },
        pedirMaisSePertoDoFim() {
            if (!this.temMais || this.carregandoMais || this.loading) return;
            const el = this.containerEl();
            if (!el) return;
            // Sem barra de rolagem (scrollHeight <= clientHeight) também conta como fim.
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - CARREGAR_MAIS_LIMIAR_PX) {
                this.$emit('carregar-mais');
            }
        },
        onColumnDragOver() {
            if (!this.statusDragEnabled) return;
            const sid = this.status && this.status.id_status_ticket;
            if (sid == null) return;
            this.$emit('column-drag-over', Number(sid));
        },
        onColumnDrop(e) {
            if (!this.statusDragEnabled) return;
            const raw = (e.dataTransfer && e.dataTransfer.getData('text/plain')) || '';
            const id_ticket = parseInt(String(raw).trim(), 10);
            if (!Number.isFinite(id_ticket)) return;
            const targetId = this.status && this.status.id_status_ticket != null
                ? Number(this.status.id_status_ticket)
                : NaN;
            if (!Number.isFinite(targetId)) return;
            this.$emit('card-drop', { id_ticket, id_status_ticket: targetId });
        }
    }
};
</script>

<style scoped>
.tickets-board-column {
    width: 280px;
    min-width: 280px;
}
.column-card {
    height: auto;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
}
.column-cards-container {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    min-height: 48px;
}
.column-cards-container--drop-target {
    outline: 2px dashed rgba(25, 118, 210, 0.55);
    outline-offset: -2px;
    background-color: rgba(25, 118, 210, 0.06);
}
</style>
